import { Component, markRaw } from "vue";

import CrownSimple from "@/images/CrownSimple.svg?component";
import Lock from "@/images/lock.svg?component";
import UserCheck from "@/images/UserCheck.svg?component";

// How a row's swatch is drawn. The distinction is the board's, not a styling
// choice: `pattern` rows are painted with a repeating chevron rather than a
// flat fill, `dashed` rows are the two the board outlines with a dashed border,
// and `icon` rows mark a glyph drawn on an appointment card rather than a
// colour behind it.
export type LegendSwatchKind = "color" | "dashed" | "pattern" | "icon";

export interface ScheduleLegendRow {
  // Keyed on the board's own status names so the swatch colour can be resolved
  // through the same getStatusColor the timeline paints with. Icon rows carry a
  // key only for the list key.
  key: string;
  label: string;
  subLabel?: string;
  description: string;
  swatch: LegendSwatchKind;
  // `pattern` rows only: which chevron artwork to draw.
  pattern?: "travel" | "late";
  // `icon` rows only: the glyph the board draws on the card. markRaw'd at the
  // definitions below: a component reference reaching a reactive prop as a plain
  // object makes Vue warn and needlessly proxy it.
  icon?: Component;
}

// The seven statuses the tenant can recolour, in the order the board's own
// customization list uses. Copy is V1's: fuller than the Scheduler 2.0 list,
// which is the point of this story.
const STATUS_ROWS: ScheduleLegendRow[] = [
  {
    key: "pending_unassigned",
    label: "Pending",
    subLabel: "(Unassigned)",
    description:
      "Is not assigned to a Technician or an installer to be worked.",
    swatch: "color"
  },
  {
    key: "pending_assigned",
    label: "Pending",
    subLabel: "(Assigned)",
    description: "Is assigned to a Technician or an installer to be worked.",
    swatch: "color"
  },
  {
    key: "accepted_pre_work",
    label: "Accepted",
    subLabel: "(Pre-Work)",
    description:
      "Technician has claimed the appointment and is preparing to start trip.",
    swatch: "color"
  },
  {
    key: "accepted",
    label: "Accepted",
    subLabel: "(Start Trip)",
    description:
      "Technician has started their trip to the location and is in transit.",
    swatch: "color"
  },
  {
    key: "running",
    label: "Running",
    description:
      "The Technician has finished their trip and is currently working at the job site.",
    swatch: "color"
  },
  {
    key: "compeleted_won_opportunity",
    label: "Completed",
    subLabel: "(Won Opportunity)",
    description:
      "The work is completed, and is tied to a won opportunity (Revenue on invoice).",
    swatch: "color"
  },
  {
    key: "compeleted_lost_opportunity",
    label: "Completed",
    subLabel: "(Lost Opportunity)",
    description:
      "The work is completed, but it’s tied to a lost opportunity (Revenue on invoice).",
    swatch: "color"
  }
];

// Everything the tenant cannot recolour. Hold and Event (available) are dashed
// because the board outlines exactly those two that way; Event (unavailable)
// shares their grey but not their border, so it stays a solid pill.
const FIXED_ROWS: ScheduleLegendRow[] = [
  {
    key: "traveling",
    label: "Travel Time",
    description: "Technician is traveling to the job site.",
    swatch: "pattern",
    pattern: "travel"
  },
  {
    key: "late",
    label: "Late",
    description: "Technician finished their trip after the arrival window.",
    swatch: "pattern",
    pattern: "late"
  },
  {
    key: "hold",
    label: "Hold",
    description:
      "A block of time reserved by the Scheduler for an unassigned appointment to run later in the same workday.",
    swatch: "dashed"
  },
  {
    key: "pinned",
    label: "Pinned",
    subLabel: "(Lock icon)",
    description:
      "Manually assigned to a specific Technician to be worked and will not be replanned by the scheduler.",
    swatch: "icon",
    icon: markRaw(Lock)
  },
  {
    key: "customer_scheduled",
    label: "Customer Scheduled",
    subLabel: "(Person icon)",
    description:
      "Scheduled by the Customer (through the widget or customer portal).",
    swatch: "icon",
    icon: markRaw(UserCheck)
  },
  // Not in the story's list. Kept because the board still draws a crown on a
  // membership customer's card, and dropping the row would leave that glyph
  // unexplained on the one surface where this key is the only key.
  {
    key: "membership",
    label: "Membership",
    subLabel: "(Crown icon)",
    description: "Customer has an active membership.",
    swatch: "icon",
    icon: markRaw(CrownSimple)
  },
  {
    key: "available_event",
    label: "Event",
    subLabel: "(available)",
    description: "Technician is available to be assigned to appointments.",
    swatch: "dashed"
  },
  {
    key: "unavailable_event",
    label: "Event",
    subLabel: "(unavailable)",
    description: "Technician is not available to be assigned appointments.",
    swatch: "color"
  }
];

// The schedule key's rows, in reading order. Deliberately carries no colour:
// what a status is painted is the board's answer, read through
// useScheduleColors at render time, so the key cannot drift from the board.
export const scheduleLegendRows = (): ScheduleLegendRow[] => [
  ...STATUS_ROWS,
  ...FIXED_ROWS
];
